import React, { useEffect, useRef, useState } from "react";
import Modal from "../../../../components/ui/Modal";
import { Button } from "../../../../components/ui/button";
import { Field, Input, Textarea } from "../../../../components/ui/Field";
import { batchApi } from "../../../../api/batchApi";
import { useToast } from "../../../../components/ui/toast/ToastProvider";
import { formatMinutes } from "../../../../utils/formatters";
import { unwrapBatch } from "../../batchAdapters";

export const validateCompletion = (values, total) => {
  const counts = [values.okParts, values.reworkParts, values.rejectParts];
  if (counts.some(value => String(value).trim() === "" || !Number.isSafeInteger(Number(value)) || Number(value) < 0)) {
    return "Enter a non-negative whole number for each part count.";
  }
  if (!Number.isSafeInteger(Number(total)) || Number(total) <= 0) return "A valid batch quantity is required.";
  if (counts.reduce((sum, value) => sum + Number(value), 0) !== Number(total)) {
    return `OK, rework and reject parts must add up to ${total}.`;
  }
  return "";
};

const CompleteBatchModal = ({ open, batch, onClose, onCompleted }) => {
  const toast = useToast();
  const [values, setValues] = useState({ okParts: 0, reworkParts: 0, rejectParts: 0, comment: "" });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const submitting = useRef(false);
  const batchRef = useRef(batch);
  batchRef.current = batch;
  const uid = batch?.uid ?? batch?.raw?.uid ?? batch?.raw?.id;

  useEffect(() => {
    if (open) {
      const current = batchRef.current;
      setValues({ okParts: current?.okParts ?? 0, reworkParts: current?.reworkParts ?? 0, rejectParts: current?.rejectParts ?? 0, comment: "" });
      setError("");
    }
  }, [open, uid]);

  if (!batch) return null;
  const totalParts = batch.totalParts ?? batch.raw?.batch_quantity;
  const canComplete = Boolean(uid) && !batch.dispatchOn && !batch.raw?.dispatch_on && ["WIP", "Hold"].includes(batch.status);
  const assigned = [values.okParts, values.reworkParts, values.rejectParts].reduce((sum, value) => sum + (Number(value) || 0), 0);
  const change = (name, value) => { setValues(current => ({ ...current, [name]: value })); setError(""); };

  const handleSubmit = async event => {
    event.preventDefault();
    if (submitting.current) return;
    const validation = validateCompletion(values, totalParts);
    if (!canComplete || validation) {
      setError(validation || "Only an active batch can be completed.");
      return;
    }
    submitting.current = true;
    setIsLoading(true);
    setError("");
    try {
      const updated = await batchApi.complete(uid, {
        ok_parts: Number(values.okParts), rework_parts: Number(values.reworkParts), reject_parts: Number(values.rejectParts), comment: values.comment.trim(),
      });
      if (!unwrapBatch(updated)?.batch_id) throw new Error("The batch completion could not be confirmed. Refresh and try again.");
      toast.success(`${batch.batchId} completed.`);
      onCompleted?.(updated);
      onClose();
    } catch (err) {
      setError(err?.message || "Unable to complete batch.");
    } finally {
      submitting.current = false;
      setIsLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={() => !submitting.current && onClose()} title={`Complete Batch ${batch.batchId}`} description="Review all parts and add final completion notes." size="lg">
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <Field label="Total Parts"><Input aria-label="Total Parts" value={totalParts ?? "—"} readOnly className="bg-slate-50" /></Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[["okParts", "OK Parts"], ["reworkParts", "Rework Parts"], ["rejectParts", "Reject Parts"]].map(([key, label]) => (
            <Field key={key} label={label} required><Input aria-label={label} type="number" min={0} max={totalParts} step={1} required value={values[key]} disabled={isLoading} onChange={event => change(key, event.target.value)} /></Field>
          ))}
        </div>
        <p className="text-xs text-slate-500" aria-live="polite">{assigned} of {totalParts} parts accounted for.</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Production Duration (Autofilled)"><Input aria-label="Production Duration (Autofilled)" value={formatMinutes(batch.currentProcessTimeMinutes)} readOnly className="bg-slate-50" /></Field>
          <Field label="Add Comment"><Textarea aria-label="Add Comment" rows={2} maxLength={2000} value={values.comment} disabled={isLoading} onChange={event => change("comment", event.target.value)} placeholder="Add final remarks or completion notes..." /></Field>
        </div>
        {error && <p role="alert" className="rounded-md bg-rose-50 p-3 text-sm text-rose-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" disabled={isLoading} onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={isLoading || !canComplete}>{isLoading ? "Completing…" : "Complete Batch"}</Button>
        </div>
      </form>
    </Modal>
  );
};
export default CompleteBatchModal;
